import {DropdownMenuPrimitive as Menu} from "ch3-ui-lib";
import {TooltipPrimitive as Tooltip} from "ch3-ui-lib";
import {ChevronDown, Grid3x3, Info, Trash2} from "lucide-react";
import type {Action} from "../../store/features/CallQueue/CallQueueService";
import {DestinationMenu, type DestinationChoice, type DestinationPick} from "./DestinationMenu";
import {KEY_NAME_MAX} from "./queueModel";
import {AudioCard, lucide, SmallButton} from "./queueUi";

interface QueueKeyCardProps {
    index: number;
    action: Action;
    freeKeys: string[];
    destination: string | null;
    error?: string;
    ringStrategy: string;
    users: DestinationChoice[];
    groups: DestinationChoice[];
    numbers: DestinationChoice[];
    onGroupsEnd?: () => void;
    playMessageSrc?: string;
    onChange: (patch: Partial<Action>) => void;
    onPick: (pick: DestinationPick) => void;
    onEditMessage: () => void;
    onRemove: () => void;
}

const fieldLabel = 'text-footnote font-medium text-ink-secondary';

/** One numeric option (Queues-EditMenu): Key, Destination, Key name. Key 0 is fixed and can't be removed. */
export const QueueKeyCard = ({index, action, freeKeys, destination, error, ringStrategy, users, groups, numbers, onGroupsEnd, playMessageSrc, onChange, onPick, onEditMessage, onRemove}: QueueKeyCardProps) => {
    const isZero = String(action.order) === '0';
    const keyName = action.keyName ?? '';
    const ringsEveryone = (action.type === 'ForwardToGroup' || action.type === 'ForwardToInternalNumber') && ringStrategy !== 'RING_ALL';

    return (
        <div className="flex flex-col gap-2 rounded-xl border border-solid border-line bg-surface p-3">
            <div className="flex items-end gap-2.5">
                <div className="flex flex-none flex-col gap-1">
                    <span className={`${fieldLabel} flex items-center gap-1`} id={`key-${index}-label`}>
                        Key
                        <KeyHint/>
                    </span>
                    {isZero ? (
                        <span aria-labelledby={`key-${index}-label`} className="box-border flex h-control-md w-[72px] items-center gap-1.5 rounded-control border border-solid border-fill-subtle bg-surface-muted px-3 text-callout text-ink">
                            <Grid3x3 size={14} aria-hidden="true" className={`${lucide} text-ink-muted`}/>0
                        </span>
                    ) : (
                        <Menu.Root modal={false}>
                            <Menu.Trigger asChild>
                                <button type="button" aria-labelledby={`key-${index}-label`} className="box-border flex h-control-md w-[72px] cursor-pointer items-center gap-1.5 rounded-control border border-solid border-fill-subtle bg-surface px-3 text-callout text-ink outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-brand-focus">
                                    <Grid3x3 size={14} aria-hidden="true" className={`${lucide} text-ink-muted`}/>
                                    <span className="flex-1 text-left">{action.order}</span>
                                    <ChevronDown size={12} aria-hidden="true" className={`${lucide} text-ink-muted`}/>
                                </button>
                            </Menu.Trigger>
                            <Menu.Portal>
                                <Menu.Content align="start" sideOffset={6} className="z-[70] grid grid-cols-3 gap-1 rounded-xl bg-zinc-950 p-1 shadow-menu outline-none">
                                    {freeKeys.map((k) => (
                                        <Menu.Item key={k} onSelect={() => onChange({order: k})}
                                                   className={`flex size-9 cursor-pointer items-center justify-center rounded-chip text-callout tabular-nums outline-none data-[highlighted]:bg-zinc-800 ${k === String(action.order) ? 'bg-brand text-white' : 'text-white'}`}>
                                            {k}
                                        </Menu.Item>
                                    ))}
                                </Menu.Content>
                            </Menu.Portal>
                        </Menu.Root>
                    )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <label htmlFor={`key-${index}-dest`} className={fieldLabel}>Destination</label>
                    <DestinationMenu id={`key-${index}-dest`} label={destination} invalid={Boolean(error && !action.type)} users={users} groups={groups} numbers={numbers} onGroupsEnd={onGroupsEnd} onPick={onPick}/>
                </div>
                {!isZero && (
                    <button type="button" aria-label={`Delete option ${action.order}`} onClick={onRemove}
                            className="flex h-control-md w-11 flex-none cursor-pointer items-center justify-center rounded-control text-brand hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                        <Trash2 size={18} aria-hidden="true" className={lucide}/>
                    </button>
                )}
            </div>

            {action.type === 'PlayMessage' && (
                playMessageSrc
                    ? (
                        <div className="flex flex-col gap-2">
                            <AudioCard title={`Message for key ${action.order}`} src={playMessageSrc}/>
                            <SmallButton onClick={onEditMessage} className="self-start">Change message</SmallButton>
                        </div>
                    )
                    : <SmallButton onClick={onEditMessage}>Set up message</SmallButton>
            )}

            <div className="flex flex-col gap-1">
                <label htmlFor={`key-${index}-name`} className={fieldLabel}>Key name</label>
                <input
                    id={`key-${index}-name`}
                    value={keyName}
                    maxLength={KEY_NAME_MAX}
                    placeholder="e.g. Help, Sales"
                    aria-invalid={Boolean(error && action.type && !keyName.trim()) || undefined}
                    aria-describedby={`key-${index}-count`}
                    onChange={(e) => onChange({keyName: e.target.value})}
                    className="box-border h-control-md w-full rounded-control border border-solid border-fill-subtle bg-surface px-3 text-callout text-ink outline-none placeholder:text-ink-muted focus:border-transparent focus:ring-2 focus:ring-brand-focus aria-invalid:border-danger-ink"
                />
                <span id={`key-${index}-count`} className="text-right text-footnote text-ink-muted">{keyName.length}/{KEY_NAME_MAX} characters</span>
            </div>

            {error && <p role="alert" className="m-0 text-footnote text-danger-ink">{error}</p>}
            {isZero && <p className="m-0 text-footnote text-ink-muted">The option under number 0 is irremovable.</p>}
            {ringsEveryone && (
                <p className="m-0 flex gap-2 rounded-control bg-brand-tint-subtle p-2.5 text-footnote text-ink-secondary">
                    <Info size={14} aria-hidden="true" className={`${lucide} mt-0.5 text-brand-ink`}/>
                    {action.type === 'ForwardToGroup'
                        ? 'Everyone in this group is called at once, as with “Ring all”, whatever the queue strategy.'
                        : 'Everyone who shares this number is called at once, as with “Ring all”, whatever the queue strategy.'}
                </p>
            )}
        </div>
    );
};

/** Queues-KeyTooltip. */
const KeyHint = () => (
    <Tooltip.Provider delayDuration={200}>
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <button type="button" aria-label="About keys" className="flex cursor-help items-center border-0 bg-transparent p-0 text-ink-muted">
                    <Info size={13} aria-hidden="true" className={lucide}/>
                </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={6} className="z-[80] max-w-[220px] rounded-chip bg-zinc-950 px-2.5 py-1.5 text-footnote text-white shadow-menu">
                    Key pressed on the caller’s phone keypad. 0 is reserved.
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    </Tooltip.Provider>
);
